<template>
	<div class="orders-analytics-container">

		<SummaryCard
			ref="initial-card"
			:cards-info="todayCardsInfo"
			:data="filteredTodayOrders"
			:previous-data="filteredTodayPreviousOrders"
			data-field-value="totalFormatted" />

		<FiltersRow ref="filters" @changed-timeframe="changedTimeframe" @switched-chart="tables => showCharts = !tables" with-table-switch :loading="isLoading" />

		<OrdersChartsAggregated :dates="periodDates" v-if="showCharts" />
		<OrdersTablesAggregated :dates="periodDates" v-else />

		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="analyticsStore.reviewsBottomSheetActive" @toggling-popup="toggleBottomSheetReviews">
			<OrdersReviews v-if="analyticsStore.reviewsBottomSheetActive" :dates="periodDates" />
		</AppBottomSheet>

	</div>
</template>

<script>
import SummaryCard from "@/console-new/components/analytics/SummaryCard.vue";
import {useAnalyticsStore} from "@/console-new/stores";
import {useUserStore} from "@/user/stores";
import {useLanguageStore} from "@/shared/stores";
import {formatDateTimeWithSeconds} from "@/shared/helpers/dates";
import FiltersRow from "@/console-new/components/analytics/FiltersRow.vue";
import OrdersChartsAggregated from "@/console-new/components/analytics/OrdersChartsAggregated.vue";
import OrdersTablesAggregated from "@/console-new/components/analytics/OrdersTablesAggregated.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import OrdersReviews from "@/console-new/components/analytics/OrdersReviews.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";

export default {
	setup() {
		const analyticsStore = useAnalyticsStore();
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		return {analyticsStore, userStore, getString: languageStore.getString};
	},
	name: "OrdersContainer",
	components: { SummaryCard, FiltersRow, OrdersChartsAggregated, OrdersTablesAggregated, AppBottomSheet, OrdersReviews },
	mixins: [WindowWidth],
	data() {
		return {
			realtimeTimeout: null,
			periodDates: {
				end: this.analyticsStore.getTargetEndDate(),
				start: this.analyticsStore.getTargetStartDate(0),
				previousStart: this.analyticsStore.getTargetPreviousStartDate(0),
				previousEnd: this.analyticsStore.getTargetPreviousEndDate(0),
				timeframeIndex: 0,
			},
			showCharts: true,
			isLoading: false,
		};
	},
	computed: {
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 820) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "780px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		filteredTodayOrders() {
			const now = new Date();
			const todayShiftStart = this.analyticsStore.getDayShiftDatetime();
			
			if (!this.analyticsStore.ordersAggregatedToday.full || this.analyticsStore.ordersAggregatedToday.full.length === 0) {
				return [];
			}
			
			const nowRounded = new Date(now);
			if (nowRounded.getMinutes() > 0 || nowRounded.getSeconds() > 0) {
				nowRounded.setTime(nowRounded.getTime() + 3600 * 1000);
			}
			nowRounded.setMinutes(0, 0, 0);
			
			const filteredData = this.analyticsStore.ordersAggregatedToday.full.filter(row => {
				const date = new Date(row.hour_bucket || row.day_bucket);
				// hour_bucket represents the END of the hour, so 03:00 contains orders from 02:00-03:00
				// We want to exclude the shift hour itself to avoid counting pre-shift orders
				return date > todayShiftStart && date <= nowRounded;
			});
			
			return filteredData.map(row => ({
				date: new Date(row.hour_bucket || row.day_bucket),
				totalFormatted: row.gross_sales / 100,
				fee: 0,
				cover_charge_num: row.cover_ctn || 0,
				orders_cnt: row.orders_cnt || 0
			}));
		},
		filteredTodayPreviousOrders() {
			const now = new Date();
			const yesterdayShiftStart = this.analyticsStore.getPreviousDayShiftDatetime();
			const todayShiftStart = this.analyticsStore.getDayShiftDatetime();
			
			if (!this.analyticsStore.ordersAggregatedToday.full || this.analyticsStore.ordersAggregatedToday.full.length === 0) {
				return [];
			}

			// Calculate time elapsed since today's shift start
			const nowRounded = new Date(now);
			if (nowRounded.getMinutes() > 0 || nowRounded.getSeconds() > 0) {
				nowRounded.setTime(nowRounded.getTime() + 3600 * 1000);
			}
			nowRounded.setMinutes(0, 0, 0);

			const elapsedTime = nowRounded.getTime() - todayShiftStart.getTime();
			const yesterdayEndTime = new Date(yesterdayShiftStart.getTime() + elapsedTime);

			const filteredData = this.analyticsStore.ordersAggregatedToday.full.filter(row => {
				const date = new Date(row.hour_bucket || row.day_bucket);
				// hour_bucket represents the END of the hour, so exclude the shift hour itself
				return date > yesterdayShiftStart && date <= yesterdayEndTime;
			});
			
			return filteredData.map(row => ({
				date: new Date(row.hour_bucket || row.day_bucket),
				totalFormatted: row.gross_sales / 100,
				fee: 0,
				cover_charge_num: row.cover_ctn || 0,
				orders_cnt: row.orders_cnt || 0
			}));
		},
		todayCardsInfo() {
			let items = [
				{
					title: this.getString("total"),
					value: this.dividedSeries.total,
					previousValue: this.dividedSeries.previousTotal,
				},
				{
					title: this.getString("orders_number"),
					value: this.dividedSeries.ordersNumber,
					previousValue: this.dividedSeries.previousOrdersNumber,
					noCurrency: true
				},
				{
					title: this.getString("average_receipt"),
					value: this.dividedSeries.averageReceipt,
					previousValue: this.dividedSeries.previousAverageReceipt,
					withCents: true
				},
			];
			if(this.userStore.currBusiness && this.userStore.currBusiness.type === "restaurant") {
				items.push({
					title: this.getString("covers"),
					value: this.dividedSeries.covers,
					previousValue: this.dividedSeries.previousCovers,
					noCurrency: true
				});
			} else {
				items.push({
					title: this.getString("best_order"),
					value: this.dividedSeries.bestOrder,
					previousValue: this.dividedSeries.previousBestOrder,
				});
			}
			return items;
		},
		dividedSeries() {
			let result = {
				total: 0,
				previousTotal: 0,
				netTotal: 0,
				previousNetTotal: 0,
				averageReceipt: 0,
				previousAverageReceipt: 0,
				ordersNumber: 0,
				previousOrdersNumber: 0,
				covers: 0,
				previousCovers: 0,
				bestOrder: 0,
				previousBestOrder: 0,
			};

			let bestOrder = 0;
			this.filteredTodayOrders.forEach(order => {
				result.total += order.totalFormatted;
				result.netTotal += order.totalFormatted - (Number(order.fee)/100);
				result.covers += order.cover_charge_num;
				if(order.totalFormatted > bestOrder) {
					bestOrder = order.totalFormatted;
				}
				result.ordersNumber += order.orders_cnt;
			});
			result.bestOrder = bestOrder;
			result.averageReceipt = result.ordersNumber ? Number(Number(result.total / result.ordersNumber).toFixed(2)) : 0;

			let previousBestOrder = 0;
			this.filteredTodayPreviousOrders.forEach(order => {
				result.previousTotal += order.totalFormatted;
				result.previousNetTotal += order.totalFormatted - (Number(order.fee)/100);
				result.previousCovers += order.cover_charge_num;
				if(order.totalFormatted > previousBestOrder) {
					previousBestOrder = order.totalFormatted;
				}
				result.previousOrdersNumber += order.orders_cnt;
			});
			result.previousBestOrder = previousBestOrder;
			result.previousAverageReceipt = result.previousOrdersNumber ? Number(Number(result.previousTotal / result.previousOrdersNumber).toFixed(2)) : 0;

			return result;
		}
	},
	methods: {
		toggleBottomSheetReviews(isOpen) {
			if(!isOpen) {
				this.analyticsStore.reviewsBottomSheetActive = false;
			}
		},
		updateTodayOrders(callback = null) {
			let now = new Date();
			let endDate = new Date(now);
			if (endDate.getMinutes() > 0 || endDate.getSeconds() > 0) {
				endDate.setTime(endDate.getTime() + 3600 * 1000);
			}
			endDate.setMinutes(0, 0, 0);

			// Fetch from yesterday's day shift to cover both today and yesterday's same period
			let startDate = this.analyticsStore.getPreviousDayShiftDatetime();
			let endDateDB = formatDateTimeWithSeconds(endDate);
			let startDateDB = formatDateTimeWithSeconds(startDate);

			this.analyticsStore.getStatsOrdersAggregated(startDateDB, endDateDB, callback, true);
		},
		updateOrders() {
			this.isLoading = true;

			let startDate = this.periodDates.previousStart;
			let endDate = this.periodDates.end;

			if(startDate) {
				// Check if current month, current year, or custom range is selected
				// Custom ranges (7) go through dual-period to handle partial months correctly
				if (this.periodDates.timeframeIndex === 2 || this.periodDates.timeframeIndex === 6 || this.periodDates.timeframeIndex === 7) {
					// Use dual-period fetching for current month/year/custom
					this.analyticsStore.getStatsOrdersAggregatedDualPeriod(this.periodDates, () => {
						this.isLoading = false;
					});
				} else {
					// Use standard single API call for other timeframes
					let endDateDB = formatDateTimeWithSeconds(endDate);
					let otherStartDateDB = formatDateTimeWithSeconds(startDate);
					this.analyticsStore.getStatsOrdersAggregated(otherStartDateDB, endDateDB, () => {
						this.isLoading = false;
					});
				}

			} else {
				this.isLoading = false;
			}
		},
		updateWeather() {

			let startDate = this.periodDates.start;
			let endDate = this.periodDates.end;

			if(startDate) {
				let endDateDB = formatDateTimeWithSeconds(endDate);
				let otherStartDateDB = formatDateTimeWithSeconds(startDate);

				this.analyticsStore.getWeather(otherStartDateDB, endDateDB);

			}
		},
		updateAll() {
			this.isLoading = true;

			this.updateOrders();
			this.updateWeather();
			this.updateTodayOrders();

		},
		changedTimeframe(start, end, previousStart, previousEnd, timeframeIndex) {
			this.periodDates.start = start;
			this.periodDates.end = end;
			this.periodDates.previousStart = previousStart;
			this.periodDates.previousEnd = previousEnd;
			this.periodDates.timeframeIndex = timeframeIndex;
			this.updateOrders();
			this.updateWeather();
		},
		scheduleNextRefresh() {
			// Clear any existing timeout
			if(this.realtimeTimeout) {
				clearTimeout(this.realtimeTimeout);
			}
			
			// Schedule the next refresh in 20 seconds
			this.realtimeTimeout = setTimeout(() => {
				this.updateTodayOrders(() => {
					// Schedule the next refresh only after fetch completes
					this.scheduleNextRefresh();
				});
			}, 1000 * 20);
		}
	},
	mounted() {
		if (this.analyticsStore.loadedOrders === false) {
			this.analyticsStore.loadedOrders = true;
			this.periodDates.start = this.$refs["filters"].targetStartDate;
			this.periodDates.end = this.$refs["filters"].targetEndDate;
			this.periodDates.previousStart = this.$refs["filters"].targetPreviousStartDate;
			this.periodDates.previousEnd = this.$refs["filters"].targetPreviousEndDate;
			this.periodDates.timeframeIndex = this.$refs["filters"].selectedTimeIndex;
			this.updateAll();
		} else {
			// wait for the animation to finish
			setTimeout(() => {
				if(this.$refs["filters"]) {
					this.periodDates.start = this.$refs["filters"].targetStartDate;
					this.periodDates.end = this.$refs["filters"].targetEndDate;
					this.periodDates.previousStart = this.$refs["filters"].targetPreviousStartDate;
					this.periodDates.previousEnd = this.$refs["filters"].targetPreviousEndDate;
					this.periodDates.timeframeIndex = this.$refs["filters"].selectedTimeIndex;
				}
				this.updateTodayOrders();
			}, 1000);
		}
		this.scheduleNextRefresh();

	},
	beforeUnmount() {
		if(this.realtimeTimeout) {
			clearTimeout(this.realtimeTimeout);
		}
	},
	watch: {
		"userStore.currBusinessId": function (newVal, oldVal) {
			if(newVal != null && newVal !== oldVal) {
				this.updateAll();
			}
		},
	}
}
</script>

<style lang="scss" scoped>
.orders-analytics-container {
	max-width: 1400px;
	margin: 0 auto;

}
</style>